<template>
  <div class="document-download">
    <h1>{{ $t('documentRequestTile.title') }}</h1>
    <div>
      <span
        v-dompurify-html="
          $t('documentRequestTile.helpTextDocumentDownload', {
            documentLinkAkb,
            documentLinkDataUsage,
            documentLinkInsuranceInfo
          })
        "
      >
      </span>

      <div class="document-actions">
        <div class="document-actions__email">
          <span class="document-actions__email-label">
            {{ $t('documentRequestTile.mail') }}:
          </span>
          <span class="mail-address">{{ userMailAddress || '' }}</span>
        </div>
        <div class="document-actions__button">
          <CustomButton
            v-if="!documentsSent"
            :buttonColor="ButtonColor.PRIMARY"
            buttonId="requestButton"
            :pendingState="documentSendButtonIsPending"
            @click="requestDocuments"
          >
            {{ $t('documentRequestTile.button.send') }}
          </CustomButton>
          <CustomButton
            v-else
            :buttonColor="ButtonColor.PRIMARY"
            :disabledState="true"
            disabled
          >
            <span class="icon-check"></span>
            {{ $t('documentRequestTile.button.requested') }}
          </CustomButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, type Ref, ref } from 'vue'
import { policyIdReg } from '@/utilsTenant/PropsValidation'
import { getEmail, getPartnerProxyId } from '@/utils/KeycloakUtils'
import { ButtonColor, CustomButton } from 'de-zurich-ui-library'
import { requestMotorDocuments } from '@/stores/motorService'

const props = defineProps({
  policyId: {
    type: String,
    required: true,
    validator(value: string): boolean {
      return policyIdReg.test(value)
    }
  },
  documentsSent: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits<{
  'documents-requested': [none: void]
  'error:documents-requested': [none: void]
}>()

const documentSendButtonIsPending = ref(false)
const userMailAddress: Ref<string | null> = ref(null)
const documentLinkAkb = ref(globalThis.ENV_CONFIG?.DOCUMENTS.ABK)
const documentLinkDataUsage = ref(globalThis.ENV_CONFIG?.DOCUMENTS.DATA_USAGE)
const documentLinkInsuranceInfo = ref(globalThis.ENV_CONFIG?.DOCUMENTS.INSURANCE_INFO)

onMounted(() => {
  userMailAddress.value = getEmail()
})

async function requestDocuments() {
  documentSendButtonIsPending.value = true

  const partnerProxyId = getPartnerProxyId()

  const params = {
    email: userMailAddress.value,
    policyId: props.policyId,
    partnerProxyId: partnerProxyId
  }

  try {
    const success = await requestMotorDocuments(params)
    if (success) {
      emit('documents-requested')
    } else {
      emit('error:documents-requested')
    }
  } catch (_error) {
    emit('error:documents-requested')
  } finally {
    documentSendButtonIsPending.value = false
  }
}
</script>

<style scoped lang="scss">
:deep(.flex-footer) {
  row-gap: 8px;
}

.document-download {
  padding-top: var(--baseline);

  .document-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc(var(--baseline) * 2);
    padding-top: calc(var(--baseline) * 1.4);
  }

  .document-actions__email {
    color: var(--c-primary-900);
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
  }

  .document-actions__email-label {
    line-height: 0.5;
  }

  .mail-address {
    color: var(--c-primary-900);
    display: block;
    font-family: var(--f-primary-bold), serif;
    margin-top: calc(var(--baseline) * 0.5);
  }

  .document-actions__button {
    display: flex;
    justify-content: flex-end;
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .document-download {
    .document-actions {
      align-items: flex-start;
      flex-direction: column;
      gap: var(--baseline);
      width: 100%;
    }

    .document-actions__button {
      width: 100%;
    }

    .document-actions__button :deep(button) {
      width: 100%;
    }
  }
}
</style>
